import { validateNotEmpty, validateEmail, validatePhoneNumber } from 'src_common/common/mobx-utils/Form2/validators';
import { SdkStaff, StaffAccountBasicDataType } from 'src/AppState/sdk/staff';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { FormModel, Result } from 'src_common/common/mobx-utils/Form2/FormModel';
import { getTranslationFor } from 'src/locale/lang';
import { action, observable, computed, makeAutoObservable } from 'mobx';
import addError from 'src/utils/error.jsx';
import { ModelsState } from 'src/AppState/Models/ModelsState';
import {
    getCountryKey,
    getPrefix,
    mobileCountryFromKey,
    mobilePrefixFromKey,
} from 'src/components/CountryDropdown/CountryDropdownNew.state';

interface PhoneNumber {
    country: string;
    prefix: string;
    number: string;
}

interface titleListType {
    key: string;
    name: string;
}

const titleList = [
    {
        key: 'mr',
        name: getTranslationFor('USER_TITLE_MR_LABEL'),
    },
    {
        key: 'mrs',
        name: getTranslationFor('USER_TITLE_MRS_LABEL'),
    },
    {
        key: 'ms',
        name: getTranslationFor('USER_TITLE_MS_LABEL'),
    },
    {
        key: 'miss',
        name: getTranslationFor('USER_TITLE_MISS_LABEL'),
    },
];

interface FormType {
    cpfInput: string | null | undefined;
    externalIdInput: string | null | undefined;
    telebetingIdInput: string | null | undefined;
    telebetingPasswordInput: string | null | undefined;
    titleInput: string;
    birthDayInput: string;
    personalIdInput: string | null | undefined;
    firstNameInput: string;
    surnameInput: string;
    fullNameInput: string | null | undefined;
    bankAccountNumberInput: string | null | undefined;
    sortCodeInput: string | null | undefined;
    emailInput: string;
    phonePrefixInput: string;
    phoneNumberInput: string;
    address1Input: string;
    address2Input: string | null | undefined;
    townInput: string;
    countyInput: string | null | undefined;
    postCodeInput: string;
    countryInput: string;
    landlinePrefixInput: string | undefined | null;
    landLineNumberInput: string | undefined | null;
}

const noEmptyValidationMessage = 'This field is required.';

const validateOtherValueIsNotEmptyString =
    (otherValueCallback: () => string, message: string) =>
    (value: string): Result<string> => {
        const otherValue = otherValueCallback();

        if (otherValue !== '' && value === '') {
            return Result.createError(message);
        }

        return Result.createOk(value);
    };

const transformNonePrefixValue = (value: string): Result<string> => {
    if (value.toLocaleLowerCase().includes('none')) {
        return Result.createOk('');
    }

    return Result.createOk(value);
};

export class PersonalDetailsTabState {
    public constructor(
        private readonly modelsState: ModelsState,
        private readonly customerId: number
    ) {
        makeAutoObservable(this);
    }

    @computed get getBasicData(): StaffAccountBasicDataType | null {
        return this.modelsState.getUserModel(this.customerId).basicData.data;
    }
}

export class PersonalDetailsTabStateInner {
    readonly cpfInput: FormInputState<string, string>;
    readonly externalIdInput: FormInputState<string, string>;
    readonly telebetingIdInput: FormInputState<string, string>;
    readonly telebetingPasswordInput: FormInputState<string, string>;
    readonly titleInput: FormInputState<string, string>;
    readonly birthDayInput: FormInputState<string, string>;
    readonly personalIdInput: FormInputState<string, string>;
    readonly firstNameInput: FormInputState<string, string>;
    readonly surnameInput: FormInputState<string, string>;
    readonly fullNameInput: FormInputState<string, string>;
    readonly bankAccountNumberInput: FormInputState<string, string>;
    readonly sortCodeInput: FormInputState<string, string>;
    readonly emailInput: FormInputState<string, string>;
    readonly phonePrefixInput: FormInputState<string, string>;
    readonly phoneNumberInput: FormInputState<string, string>;
    readonly landlinePrefixInput: FormInputState<string, string>;
    readonly landLineNumberInput: FormInputState<string, string>;
    readonly address1Input: FormInputState<string, string>;
    readonly address2Input: FormInputState<string, string>;
    readonly townInput: FormInputState<string, string>;
    readonly countyInput: FormInputState<string, string>;
    readonly postCodeInput: FormInputState<string, string>;
    readonly countryInput: FormInputState<string, string>;

    private readonly form: FormModel<FormType>;

    @observable isUpdatedSuccesfully: boolean = false;

    public constructor(
        private readonly sdkStaff: SdkStaff,
        private readonly customerId: number,
        private readonly userData: StaffAccountBasicDataType,
        private readonly userTitleOptional: boolean
    ) {
        this.cpfInput = FormInputState.new(this.userData.cpf ?? '');
        this.externalIdInput = FormInputState.new(this.userData.externalId ?? '');
        this.telebetingIdInput = FormInputState.new(this.userData.telebettingId ?? '');
        this.telebetingPasswordInput = FormInputState.new(this.userData.telebettingPassword ?? '');
        this.titleInput = FormInputState.new(this.userData.title ?? '');
        this.birthDayInput = FormInputState.new(this.userData.birthDate ?? '').map(
            validateNotEmpty(noEmptyValidationMessage)
        );
        this.personalIdInput = FormInputState.new(this.userData.personalId ?? '');
        this.firstNameInput = FormInputState.new(this.userData.firstName ?? '').map(
            validateNotEmpty(noEmptyValidationMessage)
        );
        this.surnameInput = FormInputState.new(this.userData.surname ?? '').map(
            validateNotEmpty(noEmptyValidationMessage)
        );
        this.fullNameInput = FormInputState.new(this.userData.fullName ?? '');
        this.bankAccountNumberInput = FormInputState.new(this.userData.bankAccountNumber ?? '');
        this.sortCodeInput = FormInputState.new(this.userData.bankAccountSortCode ?? '');
        this.emailInput = FormInputState.new(this.userData.email ?? '').map(validateEmail(noEmptyValidationMessage));
        this.phonePrefixInput = FormInputState.new(
            getPrefix(this.userData.mobilePhone?.prefix ?? '', this.userData.mobilePhone?.country ?? '')?.key ?? ''
        )
            .map(transformNonePrefixValue)
            .map(validateNotEmpty(noEmptyValidationMessage))
            .map(validateOtherValueIsNotEmptyString(() => this.phoneNumberInput.value, 'Please provide mobile prefix'));
        this.phoneNumberInput = FormInputState.new(this.userData.mobilePhone?.number ?? '')
            .map(validatePhoneNumber(noEmptyValidationMessage))
            .map(validateOtherValueIsNotEmptyString(() => this.phonePrefixInput.value, 'Please provide mobile prefix'));
        this.landlinePrefixInput = FormInputState.new(
            getPrefix(this.userData.landlinePhone?.prefix ?? '', this.userData.landlinePhone?.country ?? '')?.key ?? ''
        )
            .map(transformNonePrefixValue)
            .map(
                validateOtherValueIsNotEmptyString(
                    () => this.landLineNumberInput.value,
                    'Please provide landline prefix'
                )
            );
        this.landLineNumberInput = FormInputState.new(this.userData.landlinePhone?.number ?? '').map(
            validateOtherValueIsNotEmptyString(() => this.landlinePrefixInput.value, 'Please provide landline number')
        );
        this.address1Input = FormInputState.new(this.userData.addressLine1 ?? '').map(
            validateNotEmpty(noEmptyValidationMessage)
        );
        this.address2Input = FormInputState.new(this.userData.addressLine2 ?? '');
        this.townInput = FormInputState.new(this.userData.city ?? '').map(validateNotEmpty(noEmptyValidationMessage));
        this.countyInput = FormInputState.new(this.userData.county ?? '');
        this.postCodeInput = FormInputState.new(this.userData.postCode ?? '').map(
            validateNotEmpty(noEmptyValidationMessage)
        );
        this.countryInput = FormInputState.new(getCountryKey(this.userData.country ?? '')?.key ?? '').map(
            validateNotEmpty(noEmptyValidationMessage)
        );

        this.form = FormModel.group({
            cpfInput: this.cpfInput,
            externalIdInput: this.externalIdInput,
            telebetingIdInput: this.telebetingIdInput,
            telebetingPasswordInput: this.telebetingPasswordInput,
            titleInput: this.titleInput,
            birthDayInput: this.birthDayInput,
            personalIdInput: this.personalIdInput,
            firstNameInput: this.firstNameInput,
            surnameInput: this.surnameInput,
            fullNameInput: this.fullNameInput,
            bankAccountNumberInput: this.bankAccountNumberInput,
            sortCodeInput: this.sortCodeInput,
            emailInput: this.emailInput,
            phonePrefixInput: this.phonePrefixInput,
            phoneNumberInput: this.phoneNumberInput,
            address1Input: this.address1Input,
            address2Input: this.address2Input,
            townInput: this.townInput,
            countyInput: this.countyInput,
            postCodeInput: this.postCodeInput,
            countryInput: this.countryInput,
            landlinePrefixInput: this.landlinePrefixInput,
            landLineNumberInput: this.landLineNumberInput,
        });
        makeAutoObservable(this);
    }

    @action public submitUpdate = async (): Promise<void> => {
        try {
            this.isUpdatedSuccesfully = false;
            this.form.setAsVisited();

            if (this.form.result.value.type === 'ok') {
                const {
                    address1Input,
                    address2Input,
                    bankAccountNumberInput,
                    countryInput,
                    personalIdInput,
                    externalIdInput,
                    countyInput,
                    emailInput,
                    firstNameInput,
                    phonePrefixInput,
                    phoneNumberInput,
                    landLineNumberInput,
                    landlinePrefixInput,
                    postCodeInput,
                    sortCodeInput,
                    surnameInput,
                    telebetingIdInput,
                    telebetingPasswordInput,
                    birthDayInput,
                    titleInput,
                    townInput,
                    fullNameInput,
                } = this.form.result.value.data;

                const mobilePhone = this.transformPhoneNumber(phoneNumberInput, phonePrefixInput);

                if (mobilePhone === null) {
                    return;
                }

                const response = await this.sdkStaff.staffPatchUserPersonalDetail({
                    customer_id: this.customerId,
                    requestBody: {
                        addressLine1: address1Input,
                        addressLine2: address2Input,
                        birthDate: birthDayInput,
                        country: countryInput,
                        email: emailInput,
                        firstName: firstNameInput,
                        mobilePhone: mobilePhone,
                        postCode: postCodeInput,
                        surname: surnameInput,
                        fullName: fullNameInput,
                        title: titleInput,
                        bankAccountNumber: bankAccountNumberInput,
                        bankAccountSortCode: sortCodeInput,
                        city: townInput,
                        county: countyInput,
                        externalId: externalIdInput,
                        personalId: personalIdInput,
                        landlinePhone: this.transformPhoneNumber(landLineNumberInput, landlinePrefixInput),
                        telebettingId: telebetingIdInput,
                        telebettingPassword: telebetingPasswordInput,
                    },
                });

                if (response.status === '422') {
                    addError(response.errors.errors[0]?.debugDetails ?? '');
                    return;
                }

                this.isUpdatedSuccesfully = true;
            }
        } catch (e) {
            addError('Unknown Error');
        }
    };

    transformPhoneNumber(number: string | null | undefined, prefix: string | null | undefined): PhoneNumber | null {
        if (number === null || number === undefined || number === '') {
            return null;
        }

        if (prefix === null || prefix === undefined || prefix === '') {
            return null;
        }

        return {
            number,
            prefix: mobilePrefixFromKey(prefix),
            country: mobileCountryFromKey(prefix),
        };
    }

    titleList = (): titleListType[] => {
        if (this.userTitleOptional) {
            return [
                ...titleList,
                {
                    key: '',
                    name: '',
                },
            ];
        }
        return titleList;
    };
}
